import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  AccountManagerCompanies,
  AppModel,
  CustomerModel,
  IntegrationModel,
  type SupportServiceModel,
} from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { useCollectionData, useDocumentData } from "@doitintl/models-hooks";

import { useCustomerContext } from "../../Context/CustomerContext";
import { getProductsProperties, platformName } from "../../Support/utils";
import { type AnyAsset } from "../../types";
import { jsUcfirst, ticketNotification } from "../../utils/common";
import { useAvaContext } from "./AvaContext";
import { type TicketAsset } from "./types";
import { getAccountManagerEmail } from "./utils";

export const useNewQuestionAsked = () => {
  const [prevConversationId, setPrevConversationId] = useState<string>();
  const [newQuestionAsked, setNewQuestionAsked] = useState(false);
  const { conversationId } = useAvaContext();

  useEffect(() => {
    if (conversationId !== prevConversationId) {
      if (prevConversationId) {
        setNewQuestionAsked(false);
      }
      setPrevConversationId(conversationId);
    }
  }, [conversationId, setNewQuestionAsked, prevConversationId]);

  return {
    newQuestionAsked,
    setNewQuestionAsked,
  };
};

export const useConversationIdChanged = (callback: () => void) => {
  const [prevConversationId, setPrevConversationId] = useState<string>();
  const { conversationId } = useAvaContext();

  useEffect(() => {
    if (conversationId !== prevConversationId) {
      setPrevConversationId(conversationId);
      callback();
    }
  }, [conversationId, setPrevConversationId, prevConversationId, callback]);

  return prevConversationId;
};

export function useIsBigWidth() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(0);

  useLayoutEffect(() => {
    if (ref.current) {
      const observer = new ResizeObserver(([entry]) => {
        setWidth(entry.contentRect.width);
      });
      observer.observe(ref.current);
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  return { ref, bigWidth: width > 1100, width };
}

export const usePlatformList = () => {
  const ref = getCollection(IntegrationModel).doc("zendesk").collection("ticketFields").doc("platforms");
  const [ticketsFields] = useDocumentData(ref);
  return ticketsFields?.values || [];
};

export const useCategoryList = () => {
  const ref = getCollection(AppModel).doc("support").collection("services").where("blacklisted", "==", false);
  const [categories] = useCollectionData(ref);
  return categories || [];
};

export const useCategoryListFromPlatform = (categories: SupportServiceModel[], platform: string) =>
  useMemo(
    () => categories?.filter((category) => category.platform === platformName(platform)) || [],
    [categories, platform]
  );

export const filterAssetsByPlatform = (assets: Array<AnyAsset>, platform: string) => {
  const parsedAssets = assets?.map((asset) => getProductsProperties(asset.data)) || [];
  const filteredAssets =
    parsedAssets?.filter((asset) => {
      if (asset === null) {
        return false;
      }
      if (platform === "amazon_web_services") {
        return asset.asset === "amazon-web-services";
      } else if (platform === "google_cloud_platform") {
        return asset.asset === "google-cloud-project";
      }
      return platformName(platform) === asset.asset;
    }) || [];

  // delete the duplicate
  const filteredAssetsNoDuplicates = Array.from(new Set(filteredAssets.map((asset) => JSON.stringify(asset)))).map(
    (asset) => JSON.parse(asset) as TicketAsset
  );

  return filteredAssetsNoDuplicates;
};

export const useAccountManagerEmails = (platform: string, urgency: string) => {
  const [accountManagerEmails, setAccountManagerEmails] = useState<string[]>([]);

  const { customer } = useCustomerContext();
  const [defaultEmails] = useDocumentData(
    getCollection(CustomerModel).doc(customer.id).collection("defaultEmails").doc("emailCC")
  );

  useEffect(() => {
    const gsuite = "google_g_suite";
    const pVal = ticketNotification.find((item) => item.name === jsUcfirst(urgency));

    if (!customer.accountTeam || !pVal) {
      return;
    }

    const validAMs = customer.accountTeam.filter(
      (item) => item.supportNotificationLevel && item.supportNotificationLevel <= pVal?.value
    );
    const doitAMs = validAMs.filter((item) => item.company.includes(AccountManagerCompanies.DOIT));
    const tmpPlatform = platform === gsuite ? AccountManagerCompanies.GCP : platform;
    const vendorsAM = validAMs.filter((item) => item.company.includes(tmpPlatform));
    const ams = [...doitAMs, ...vendorsAM];

    Promise.all(ams.map((item) => getAccountManagerEmail(item.ref))).then((emails) => {
      setAccountManagerEmails((prevEmails) => [...prevEmails, ...emails.filter((email) => email !== undefined)]);
    });
  }, [customer, platform, urgency]);

  const allEmails = useMemo(() => {
    const defaultEmailsList = defaultEmails ? Object.values(defaultEmails) : [];
    return [...accountManagerEmails, ...defaultEmailsList];
  }, [accountManagerEmails, defaultEmails]);

  return allEmails;
};
